import React, { useState } from 'react';

import { Form } from 'antd';
import { FormInstance } from 'antd/es/form';
import styled from 'styled-components';

import GradientDivider from 'components/lib/GradientDivider';
import { COMMUNITY_RENTAL_TYPE } from 'constants/rentalTypes';
import LocationSearchInput from 'formComponents/LocationSearchInput';
import { CANADIAN_PROVINCES, US_STATES } from 'helpers/constants';
import { normalizeCommunityWebsiteUrl } from 'helpers/urls';

import BasicInfoEditFormAggregators from './BasicInfoEditFormAggregators';
import BasicInfoEditFormGeneral from './BasicInfoEditFormGeneral';
import BasicInfoEditFormRentalType from './BasicInfoEditFormRentalType';
import { COMMUNITY_CONTRACT_TYPE, CONTRACT_SLUG_PREFIX } from '../../../../constants';
import { ContractDataRelationship } from '../../../../types/communities';

type CommunityWithContracts = {
  contracts_data_relationships?: ContractDataRelationship[];
};

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  height: 100%;
  flex-direction: column;
  padding: 10px 31px;
  overflow: auto;

  .ant-form-large .ant-form-item .ant-form-item-label > label {
    color: var(--text-primary);
    font-family: var(--font-bold);
    min-width: 150px;
    display: flex;
    justify-content: flex-end;
  }
  .ant-form-item-explain-error {
    color: var(--negative-orange);
    font-size: 12px;
  }
  .ant-form-item .ant-form-item-label > label::after {
    content: '';
  }
`;

interface Props {
  form?: FormInstance;
  initialValues?: any;
  community?: CommunityWithContracts;
  handleValuesChanged: () => void;
  handleSubmit: (values: any) => void;
}

const BasicInfoEditForm = ({ form, initialValues, community, handleSubmit, handleValuesChanged }: Props) => {
  const [location, setLocation] = useState<any>(null);
  const [contractsError, setContractsError] = useState<string | undefined>(undefined);

  const relationshipHasData = (rel: ContractDataRelationship) =>
    Boolean(rel.accommodations?.length || rel.fees?.length || rel.care_fees?.length);

  const hasRentalSnapshots = community?.contracts_data_relationships?.some(
    (rel) =>
      relationshipHasData(rel) && rel.community_contracts.some((cc) => cc.type === COMMUNITY_CONTRACT_TYPE.RENTAL)
  );
  const hasCCRCSnapshots = community?.contracts_data_relationships?.some(
    (rel) =>
      relationshipHasData(rel) && rel.community_contracts.some((cc) => cc.type !== COMMUNITY_CONTRACT_TYPE.RENTAL)
  );

  const onSubmit = (values: any) => {
    if (location) {
      values.latitude = location?.lat || '';
      values.longitude = location?.lng || '';
    }
    handleSubmit(values);
  };

  const onValuesChanged = () => {
    handleValuesChanged();
  };

  const handleSetLocation = (data: any) => {
    if (!data) return;

    const { name, formatted_address, city, state, zip_code, place_id, phone_number, website, lat, lng } = data;

    const validState = [...US_STATES, ...CANADIAN_PROVINCES].some((o) => state == o.value);
    form?.setFieldsValue({
      comunity_name: name,
      address: formatted_address,
      city,
      state: validState ? state : '',
      zip_code,
      google_map_location: place_id,
      phone_number,
      website: normalizeCommunityWebsiteUrl(website),
    });

    if (lat && lng) {
      setLocation({ lat, lng });
    }
    handleValuesChanged();
  };

  const validateValues = (values: any) => {
    const contracts = values.contracts || {};
    const rentalTypes: number[] = values.rental_types || [];
    const isCCRC = rentalTypes.includes(COMMUNITY_RENTAL_TYPE.CCRC);
    const hasAtLeastOne = Object.values(contracts).some((entries) => Array.isArray(entries) && entries.length > 0);

    if (isCCRC && !hasAtLeastOne) {
      setContractsError('At least one refund entry is required.');
      return false;
    }

    setContractsError(undefined);
    return true;
  };

  return (
    <Container className={'mini-scroll'}>
      <Form
        form={form}
        onValuesChange={onValuesChanged}
        onFinish={(values) => {
          if (validateValues(values)) {
            onSubmit(values);
          }
        }}
        requiredMark={false}
        initialValues={
          initialValues || {
            comunity_name: '',
            address: '',
            city: '',
            state: '',
            zip_code: '',
            phone_number: '',
            website: '',
            notes: '',
            year_opened: '',
            rental_types: [COMMUNITY_RENTAL_TYPE.RENTAL],
            google_map_location: '',
            a_place_for_mom_id: '',
            caring_url: '',
            contracts: {
              [`${CONTRACT_SLUG_PREFIX}-${COMMUNITY_CONTRACT_TYPE.TYPE_A}`]: [null, null, null],
              [`${CONTRACT_SLUG_PREFIX}-${COMMUNITY_CONTRACT_TYPE.TYPE_B}`]: [null, null, null],
              [`${CONTRACT_SLUG_PREFIX}-${COMMUNITY_CONTRACT_TYPE.TYPE_C}`]: [null, null, null],
            },
          }
        }
      >
        <Form.Item
          name="comunity_name"
          label="Community Name"
          rules={[{ required: true, message: 'Enter Community Name' }]}
        >
          <LocationSearchInput
            getLocationBySearch={handleSetLocation}
            placeholder="Enter Name or select from Google Places"
            defaultValue={initialValues?.comunity_name}
            style={{ borderRadius: '0px', height: '36px', boxShadow: '0px', backGroundColor: 'var(--background-gray)' }}
            updateCommunityName={(value: string) => {
              form?.setFieldValue('comunity_name', value);
              handleValuesChanged();
            }}
          />
        </Form.Item>
        <BasicInfoEditFormGeneral />
        <GradientDivider className={'thin'} />
        <BasicInfoEditFormRentalType
          error={contractsError}
          disableRental={hasRentalSnapshots}
          disableCCRC={hasCCRCSnapshots}
        />
        <GradientDivider className={'thin'} />
        <BasicInfoEditFormAggregators />
      </Form>
    </Container>
  );
};

export default BasicInfoEditForm;
